import { css } from "@emotion/css";
import type { PageTitleBadge } from "@octopusdeploy/design-system-components";
import { Button, CheckboxNew, ExternalLink } from "@octopusdeploy/design-system-components";
import { Permission, SettingsIdValues } from "@octopusdeploy/octopus-server-client";
import type { SensitiveValue, MetadataTypeCollection, Repository, SettingsMetadataItemResource } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { OctoLink } from "@octopusdeploy/utilities";
import pluralize from "pluralize";
import { useConnectionDrawer, useConnectivityTestUrl, validateIntegrationSettings } from "~/areas/configuration/components/Settings/shared/integrationSettings";
import { repository } from "~/clientInstance";
import { isLicensedFeatureAvailable } from "~/components/LicensedFeature/LicensedFeatureContext";
import { PageContent } from "~/components/PageContent/PageContent";
import { RemoveItemsList } from "~/components/RemoveItemsList/RemoveItemsList";
import Section from "~/components/Section/index";
import { RequestFeatureTrialUpsellDialog } from "~/components/UpsellDialog/RequestFeatureTrialUpsellDialog";
import { UpsellServiceNowIntegrationIllustration } from "~/components/UpsellDialog/UpsellServiceNowIntegrationIllustration";
import { Form, useForm } from "~/components/form/Form/Form";
import { ExpandableFormSection, Summary } from "~/components/form/index";
import { ServiceNowIntegrationDrawer } from "./ServiceNowIntegrationDrawer";
export interface ServiceNowIntegrationConnection {
    Id: string | undefined;
    ConnectionName: string | undefined;
    BaseUrl: string | undefined;
    OAuthClientId: string | undefined;
    OAuthClientSecret: SensitiveValue;
    Username: string | undefined;
    UserPassword: SensitiveValue;
}
export interface ServiceNowIntegrationSettings {
    Id: string;
    IsEnabled: boolean;
    WorkNotesIsEnabled: boolean;
    Connections: ServiceNowIntegrationConnection[];
}
class ConnectionRemoveItemsList extends RemoveItemsList<ServiceNowIntegrationConnection> {
}
interface ServiceNowIntegrationSettingsPageLoaderData {
    settingsMetadata: SettingsMetadataItemResource;
    types: MetadataTypeCollection;
    values: ServiceNowIntegrationSettings;
    isServiceNowIntegrationFeatureAvailable: boolean;
    isTrial: boolean;
}
export async function serviceNowIntegrationSettingsPageLoader(repository: Repository) {
    const settingsResponse = await repository.Settings.getById(SettingsIdValues.ServiceNowIntegration);
    const metadata = repository.Settings.getMetadata(settingsResponse);
    const values = repository.Settings.getValues(settingsResponse);
    const isServiceNowIntegrationFeatureAvailable = isLicensedFeatureAvailable("ServiceNowIntegration");
    const licenseStatusAsync = repository.Licenses.getCurrentStatus();
    return {
        settingsMetadata: settingsResponse,
        types: await metadata,
        values: await values,
        isServiceNowIntegrationFeatureAvailable: await isServiceNowIntegrationFeatureAvailable,
        isTrial: (await licenseStatusAsync).IsTrial,
    };
}
interface ServiceNowIntegrationSettingsPageProps {
    loaderData: ServiceNowIntegrationSettingsPageLoaderData;
}
export function ServiceNowIntegrationSettingsPage({ loaderData }: ServiceNowIntegrationSettingsPageProps) {
    const { model, setModel, formProps, createSaveAction } = useForm({
        formName: "ServiceNow Integration",
        initialModel: loaderData.values,
        onSubmit: async (repository, settings) => {
            validateIntegrationSettings(loaderData.types, settings);
            return repository.Settings.saveValues(loaderData.settingsMetadata, settings);
        },
        updateFormModelAfterSubmit: (model) => model,
        snackbarSaveText: "ServiceNow Integration updated",
        submitPermission: { permission: [Permission.ConfigureServer] },
    });
    const connectionDrawer = useConnectionDrawer<ServiceNowIntegrationConnection>();
    const connectivityTestUrl = useConnectivityTestUrl(loaderData.types, "ServiceNowIntegrationConnectionResource", "UserPassword");
    const addConnection = connectionDrawer.createAddConnection(setModel);
    const removeConnection = connectionDrawer.createRemoveConnection(setModel);
    const openConnectionDrawer = (connection?: ServiceNowIntegrationConnection) => connectionDrawer.openConnectionDrawer(connection, model.Connections);
    const isEnabledHelp = (<div>
            If enabled, you can turn on change control on your <strong>projects</strong> and <strong>environments</strong> so that certain deployments require an approved ServiceNow Change Request.
        </div>);
    const tooltipContent = loaderData.isServiceNowIntegrationFeatureAvailable ? undefined : "Request a trial to try the ServiceNow Integration.";
    const badgeConfig: PageTitleBadge | undefined = loaderData.isTrial ? { variant: "upsell", label: "Enterprise" } : undefined;
    return (<PageContent header={{ primaryAction: { ...createSaveAction(), extraContext: tooltipContent }, title: "ServiceNow Integration", badge: badgeConfig, breadcrumbs: [{ label: "Settings", pageUrl: links.settingsPage.generateUrl() }] }}>
            <Section className={styles.settingOverviewSection}>
                {loaderData.isServiceNowIntegrationFeatureAvailable ? (<p className={styles.settingOverviewText}>
                        Integrate ServiceNow Change Requests into your deployments. <ExternalLink href="ServiceNowIntegration">Learn more</ExternalLink>.
                    </p>) : (<RequestFeatureTrialUpsellDialog triggerVariant="inline-message" triggerContent="The ServiceNow integration is available on the Enterprise tier." upsellDialogTitle="Enforce change approvals automatically with the ServiceNow integration" docsLabel="Docs: ServiceNow Integration" docsLink={OctoLink.Create("ServiceNowIntegration")} requestTrialDialogTitle="Request a free trial of the ServiceNow integration" featureName="ServiceNow Integration" onRequestTrial={async () => {
                await repository.Settings.requestServiceNowIntegrationTrial();
            }} upsellDialogSidePanelImage={<UpsellServiceNowIntegrationIllustration />}>
                        <p>The ServiceNow integration blocks deployments or runbooks until there's an approved change request. It links to existing requests or automatically creates new ones, so you stay compliant without the manual work.</p>
                        <p>Available on the Enterprise tier.</p>
                    </RequestFeatureTrialUpsellDialog>)}
            </Section>
            <Form {...formProps} expandAllOnMount>
                <ExpandableFormSection key="IsEnabled" errorKey="IsEnabled" title="Enabled" summary={model.IsEnabled ? Summary.summary("Yes") : Summary.summary("No")} help={isEnabledHelp} expandable>
                    <CheckboxNew value={!!model.IsEnabled} disabled={!loaderData.isServiceNowIntegrationFeatureAvailable} onChange={(IsEnabled) => setModel((prev) => ({ ...prev, IsEnabled }))} label="Enabled"/>
                </ExpandableFormSection>
                <ExpandableFormSection key="Connections" errorKey="Connections" title="Connections" summary={model.Connections?.length ? Summary.summary(`${model.Connections?.length} ${pluralize("connection", model.Connections?.length)}`) : Summary.summary("No connections")} help="Connect your Octopus instance to one or more ServiceNow instances." expandable>
                    <div>
                        <ConnectionRemoveItemsList empty={""} onRow={(data, index) => <div key={index}>{data.ConnectionName}</div>} data={model.Connections || []} listActions={[<Button label="Add Connection" importance="secondary" disabled={!loaderData.isServiceNowIntegrationFeatureAvailable} onClick={() => openConnectionDrawer()}/>]} onRemoveRow={loaderData.isServiceNowIntegrationFeatureAvailable ? (connection) => removeConnection(connection) : undefined} onRowTouch={(connection) => openConnectionDrawer(connection)}/>
                        <ServiceNowIntegrationDrawer isOpen={connectionDrawer.isConnectionDrawerOpen} isEdit={connectionDrawer.isEdit} isDisabled={!loaderData.isServiceNowIntegrationFeatureAvailable} onSuccess={addConnection} onClose={connectionDrawer.closeConnectionDrawer} connection={connectionDrawer.selectedConnection} connectivityTestUrl={connectivityTestUrl}/>
                    </div>
                </ExpandableFormSection>
                <ExpandableFormSection key="WorkNotesIsEnabled" errorKey="WorkNotesIsEnabled" title="Work Notes Enabled" summary={model.WorkNotesIsEnabled ? Summary.summary("Yes") : Summary.summary("No")} help="If enabled, work notes will be added to the linked Change Request." expandable>
                    <CheckboxNew value={!!model.WorkNotesIsEnabled} disabled={!loaderData.isServiceNowIntegrationFeatureAvailable} onChange={(WorkNotesIsEnabled) => setModel((prev) => ({ ...prev, WorkNotesIsEnabled }))} label="Work Notes Enabled"/>
                </ExpandableFormSection>
            </Form>
        </PageContent>);
}
const styles = {
    settingOverviewSection: css({
        margin: "0 !important",
    }),
    settingOverviewText: css({
        margin: 0,
    }),
};
